import type { Fee } from "../../services/financialService";
import type { ExportColumn } from "../../utils/exportTable";

/** Fee categories in the order the create and edit forms offer them. */
export const FEE_CATEGORIES = [
  ["tuition", "Tuition"],
  ["transport", "Transport"],
  ["boarding", "Boarding"],
  ["cafeteria", "Cafeteria"],
  ["uniform", "Uniform"],
  ["library", "Library"],
  ["sports", "Sports"],
  ["exam", "Examination"],
  ["other", "Other"],
] as const;

export const FEE_ORDERINGS = {
  name: "Name (A–Z)",
  "-amount": "Amount (highest first)",
  amount: "Amount (lowest first)",
  category: "Category",
} as const;

export const FEE_PAGE_SIZES = [10, 25, 50];

export interface FeeFilters {
  search: string;
  category: string;
  status: "all" | "active" | "inactive";
  appliesTo: "" | "all" | "day" | "boarding";
  terms: "" | "installments" | "full";
  ordering: keyof typeof FEE_ORDERINGS;
}

export const EMPTY_FEE_FILTERS: FeeFilters = {
  search: "",
  category: "",
  status: "all",
  appliesTo: "",
  terms: "",
  ordering: "name",
};

export const feeCategoryLabel = (type: string) =>
  FEE_CATEGORIES.find(([value]) => value === type)?.[1] ?? type;

export const feeAudienceLabel = (fee: Fee) =>
  `${fee.applies_to === "day" ? "Day students" : fee.applies_to === "boarding" ? "Boarders" : "Day and boarding"}${
    fee.transport_only ? ", school transport only" : ""
  }`;

export const feeTermsLabel = (fee: Fee) =>
  fee.allows_partial_payment ? "Installments allowed" : "Pay in full";

/** Filters that narrow the list; the status switch and sort order don't count. */
export const countFeeFilters = (filters: FeeFilters) =>
  [filters.search.trim(), filters.category, filters.appliesTo, filters.terms].filter(Boolean).length;

/** Everything except the status switch, so its counts show what each choice would give. */
function matchesNonStatusFilters(fee: Fee, filters: FeeFilters) {
  const search = filters.search.trim().toLowerCase();
  if (search && !`${fee.name} ${fee.description ?? ""}`.toLowerCase().includes(search)) return false;
  if (filters.category && fee.fee_type !== filters.category) return false;
  if (filters.appliesTo && (fee.applies_to || "all") !== filters.appliesTo) return false;
  if (filters.terms === "installments" && !fee.allows_partial_payment) return false;
  if (filters.terms === "full" && fee.allows_partial_payment) return false;
  return true;
}

export function feeStatusCounts(fees: Fee[], filters: FeeFilters) {
  const matching = fees.filter((fee) => matchesNonStatusFilters(fee, filters));
  const active = matching.filter((fee) => fee.is_active).length;
  return { all: matching.length, active, inactive: matching.length - active };
}

export function filterFees(fees: Fee[], filters: FeeFilters): Fee[] {
  const rows = fees.filter(
    (fee) =>
      matchesNonStatusFilters(fee, filters) &&
      (filters.status === "all" || fee.is_active === (filters.status === "active")),
  );
  const byName = (a: Fee, b: Fee) => a.name.localeCompare(b.name);
  const compare: Record<FeeFilters["ordering"], (a: Fee, b: Fee) => number> = {
    name: byName,
    "-amount": (a, b) => Number(b.amount) - Number(a.amount) || byName(a, b),
    amount: (a, b) => Number(a.amount) - Number(b.amount) || byName(a, b),
    category: (a, b) =>
      FEE_CATEGORIES.findIndex(([value]) => value === a.fee_type) -
        FEE_CATEGORIES.findIndex(([value]) => value === b.fee_type) || byName(a, b),
  };
  return rows.sort(compare[filters.ordering]);
}

export function feeExportColumns(incomeAccount: (fee: Fee) => string): ExportColumn<Fee>[] {
  return [
    { header: "Fee", accessor: (fee) => fee.name },
    { header: "Category", accessor: (fee) => feeCategoryLabel(fee.fee_type) },
    { header: "Description", accessor: (fee) => fee.description || "" },
    { header: "Who pays", accessor: feeAudienceLabel },
    { header: "Payment terms", accessor: feeTermsLabel },
    { header: "Income account", accessor: incomeAccount },
    { header: "Amount (UGX)", accessor: (fee) => Number(fee.amount) },
    { header: "Status", accessor: (fee) => (fee.is_active ? "Active" : "Inactive") },
  ];
}
